import React, { useState, useEffect, useContext } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { AuthContext } from "../context/AuthContext.js";
import "./Config.css";

const Config = ({
  categoria,
  setCategoria,
  descripcion,
  setDescripcion,
  handleSaveConfig,
  setMedia,
}) => {
  const [categorias, setCategorias] = useState([]);
  const [experiencia, setExperiencia] = useState("");
  const [logros, setLogros] = useState("");
  const [redes, setRedes] = useState({
    facebook: "",
    instagram: "",
    linkedin: "",
  });
  const [alerta, setAlerta] = useState(null);
  const [media, setMediaFile] = useState(null); // Archivo seleccionado
  const [isLoading, setIsLoading] = useState(false); // Estado de carga
  const { currentUser } = useContext(AuthContext);

  useEffect(() => {
    const fetchCategorias = async () => {
      const db = getFirestore();
      const categoriasCollection = collection(db, "categorias");
      try {
        const snapshot = await getDocs(categoriasCollection);
        const categoriasData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCategorias(categoriasData);
      } catch (error) {
        console.error("Error al obtener las categorías:", error);
      }
    };

    const fetchUserData = async () => {
      if (!currentUser) return;

      const db = getFirestore();
      const userRef = doc(db, "users", currentUser.uid);

      try {
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setCategoria(userData.profession || "");
          setDescripcion(userData.description || "");
          setExperiencia(userData.experience || "");
          setLogros(userData.achievements || "");
          setRedes({
            facebook: userData.socialMedia?.facebook || "",
            instagram: userData.socialMedia?.instagram || "",
            linkedin: userData.socialMedia?.linkedin || "",
          });
          setMedia(userData.imgprof || null); // Cargar imagen/video actual
        }
      } catch (error) {
        console.error("Error al obtener los datos del usuario:", error);
      }
    };

    fetchCategorias();
    fetchUserData();
  }, [currentUser, setCategoria, setDescripcion]);

  const mostrarAlerta = (mensaje, tipo) => {
    setAlerta({ mensaje, tipo });
    setTimeout(() => setAlerta(null), 5000);
  };

  const handleFileUpload = async () => {
    if (!media) {
      mostrarAlerta("Por favor selecciona un archivo.", "error");
      return null;
    }

    if (media.size > 50 * 1024 * 1024) {
      mostrarAlerta("El archivo no debe superar los 50 MB.", "error");
      return null;
    }

    const isVideo = media.type.startsWith("video/");
    if (isVideo) {
      const videoElement = document.createElement("video");
      videoElement.src = URL.createObjectURL(media);
      await new Promise((resolve) => {
        videoElement.onloadedmetadata = () => {
          if (videoElement.duration > 20) {
            mostrarAlerta("El video no debe durar más de 20 segundos.", "error");
            resolve(false);
          } else {
            resolve(true);
          }
        };
      });
    }

    try {
      const storage = getStorage();
      const fileName = `${currentUser.uid}-${media.name}`;
      const fileRef = ref(storage, `users/${fileName}`);

      // Subir archivo
      await uploadBytes(fileRef, media);
      const fileURL = await getDownloadURL(fileRef);

      return { fileName, fileURL };
    } catch (error) {
      console.error("Error al subir el archivo:", error);
      mostrarAlerta("Error al subir el archivo.", "error");
      return null;
    }
  };

  const handleDeleteOldFile = async (oldFileName) => {
    if (oldFileName) {
      const storage = getStorage();
      const oldFileRef = ref(storage, `users/${oldFileName}`);
      try {
        await deleteObject(oldFileRef);
      } catch (error) {
        console.error("Error al eliminar el archivo anterior:", error);
      }
    }
  };

  const handleSaveUserData = async () => {
    if (!currentUser) {
      mostrarAlerta("No hay un usuario autenticado.", "error");
      return;
    }
    setIsLoading(true); // Inicia el estado de carga

    const uploadedMedia = await handleFileUpload();
    if (!uploadedMedia) {
      setIsLoading(false); // Finaliza el estado de carga en caso de error
      return;
    }

    const db = getFirestore();
    const userRef = doc(db, "users", currentUser.uid);

    try {
      const currentUserData = (await getDoc(userRef)).data();
      // Elimina el archivo anterior si existe
      await handleDeleteOldFile(currentUserData.imgprof?.fileName);

      // Guarda los datos actualizados
      await updateDoc(userRef, {
        profession: categoria,
        description: descripcion,
        experience: experiencia,
        achievements: logros,
        socialMedia: redes,
        imgprof: uploadedMedia, // Guarda el objeto { fileName, fileURL }
      });

      mostrarAlerta("Datos guardados exitosamente.", "success");

      // Verifica si handleSaveConfig está definido y es una función antes de llamarla
      if (handleSaveConfig && typeof handleSaveConfig === "function") {
        try {
          handleSaveConfig(); // Asegúrate de que esta función no reciba el evento por error.
        } catch (error) {
          console.error("Error al ejecutar handleSaveConfig:", error);
        }
      }
    } catch (error) {
      console.error("Error al actualizar los datos del usuario:", error);
      mostrarAlerta("Ocurrió un error al guardar los datos.", "error");
    } finally {
      setIsLoading(false); // Finaliza el estado de carga
    }
  };
  

  return (
    <div className="config-tab">
      <h3>Configuración Profesional</h3>

      <form
        className="config-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSaveUserData();
        }}
      >
        <div className="form-group">
          <label htmlFor="categoria">Categoría de Servicios</label>
          <select
            id="categoria"
            className="form-control"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            required
          >
            <option value="">Selecciona una categoría</option>
            {categorias.map((cat) => (
              <option key={cat.id} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="descripcion">Descripción de Servicios</label>
          <textarea
            id="descripcion"
            className="form-control"
            rows="4"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          ></textarea>
        </div>

        <div className="form-group">
          <label htmlFor="media">Foto o Video Profesional</label>
          <input
            type="file"
            id="media"
            className="form-control"
            accept="image/*,video/*"
            onChange={(e) => setMediaFile(e.target.files[0])}
          />
        </div>

        <div className="form-group">
          <label htmlFor="experiencia">Experiencia Laboral</label>
          <textarea
            id="experiencia"
            className="form-control"
            rows="4"
            value={experiencia}
            onChange={(e) => setExperiencia(e.target.value)}
          ></textarea>
        </div>

        <div className="form-group">
          <label htmlFor="logros">Logros Destacados</label>
          <textarea
            id="logros"
            className="form-control"
            rows="4"
            value={logros}
            onChange={(e) => setLogros(e.target.value)}
          ></textarea>
        </div>

        <div className="form-group">
          <label htmlFor="redes">Redes Sociales</label>
          <input
            type="text"
            className="form-control"
            placeholder="Facebook URL"
            value={redes.facebook}
            onChange={(e) => setRedes({ ...redes, facebook: e.target.value })}
          />
          <input
            type="text"
            className="form-control"
            placeholder="Instagram URL"
            value={redes.instagram}
            onChange={(e) => setRedes({ ...redes, instagram: e.target.value })}
          />
          <input
            type="text"
            className="form-control"
            placeholder="LinkedIn URL"
            value={redes.linkedin}
            onChange={(e) => setRedes({ ...redes, linkedin: e.target.value })}
          />
        </div>
        {isLoading && <div className="loading-message">Subiendo archivos...</div>}

        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? "Guardando..." : "Guardar Cambios"}
        </button>
      </form>

      {alerta && (
        <div className={`alert alert-${alerta.tipo}`}>
          {alerta.mensaje}
        </div>
      )}

      
    </div>
  );
};

export default Config;
